import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import _ from 'lodash'

import { brandCurrencySuffixes, isRailBookers } from '../../configuration/brand'
// import { getRAQFormUrl } from '../../configuration/deeplinking'
import labels from '../../configuration/labels'
import useWindowDimensions from '../../hooks/useWindowDimensions'
import { toggleAccommodationModal } from '../../redux/actions/toggleAccommodationModal'
import { getBrand, getDisplayBookButtons, getShouldDisplayEasyQuoteButton, getPurlButtonInfo } from '../../redux/reducers/configuration'
import {
  getTour,
  getTourBookingPath,
  getTourDetailsForSoftripGetaway,
} from '../../redux/reducers/tourDetails'
import { addBrandCurrencyPrefix } from '../../utilities/currency/formatPrice'
import { onEnter } from '../../utilities/accessibility/onEnter'

const NAV_BAR_OPTIONS = [
    {label: labels.stickyNavOverview, target: 'tour-overview'},
    {label: labels.stickyNavItinerary, target: 'tour-itinerary'},
    {label: labels.stickyNavTripDetails, target: 'tour-details'},
    {label: isRailBookers() ? labels.stickyNavTrains : labels.stickyNavOnboard, target: 'tour-onboard'},
]
const AMTRAK_GETAWAY_NAV_BAR_OPTIONS = [
    {label: labels.stickyNavOverview, target: 'tour-overview'},
    {label: labels.stickyNavTripDetails, target: 'tour-details'},
]

const TourActions = props => {
    const { references } = props

    const dimensions = useWindowDimensions()
    const dispatch = useDispatch()
    const brand = useSelector(getBrand)
    const displayBookButtons = useSelector(getDisplayBookButtons)
    const shouldDisplayEasyQuoteButton = useSelector(getShouldDisplayEasyQuoteButton)
    const purlButtonInfo = useSelector(getPurlButtonInfo)
    const softripBookingPath = useSelector(getTourBookingPath)
    const softripBookingDetails = useSelector(getTourDetailsForSoftripGetaway)
    const tour = useSelector(getTour)
    const [curTab, setCurTab] = useState('tour-overview')
    const [isSticky, _setIsSticky] = useState(false)
    const stickyRef = useRef(isSticky)
    const setIsSticky = data => {
        stickyRef.current = data
        _setIsSticky(data)
      };

  const actionBar = useRef(null)

  const isElementInViewport = useCallback((el) => {
    if (!el) {
      return false
    }
    var rect = el.getBoundingClientRect()

    if (!rect) {
      return false
    }

    // take into account the top nav, not my own height
    const navBarHeight = 100
        
    return (rect.y - navBarHeight) >= 0 && (rect.top - navBarHeight) >= 0
  }, [])

  const onVisibilityChange = useCallback((el, callback) => {
    var visible = isElementInViewport(el)
    if (typeof callback === 'function') {
      callback(visible)
    }
  }, [isElementInViewport])

  const handler = useCallback((el) => {
    onVisibilityChange(actionBar.current, (visible) => {
      if (!visible && !stickyRef.current) {
        actionBar.current.style.minHeight = `${_.get(actionBar, 'current.firstChild.clientHeight')}px`
        setIsSticky(true)
      } else if (visible && stickyRef.current) {
        setIsSticky(false)
      }
    })
    references.forEach((value, index) => {
        if(isElementAbove(value.current)) {
            setCurTab(value.current.id)
        }
    })
  }, [onVisibilityChange, references])

  const isElementAbove = (el) => {
    if (!el) {
        return false
    }
    var rect = el.getBoundingClientRect()
    if (!rect) {
        return false
    }

    // take into account my own height
    const navBarHeight = _.get(actionBar, 'current.firstChild.clientHeight')
    const yOffset = el.id === 'tour-itinerary' ? navBarHeight : 0

    return (rect.y - navBarHeight - yOffset) <= 0 && (rect.top - navBarHeight - yOffset) <= 0
  }

  useEffect(() => {
    window.addEventListener('DOMContentLoaded', handler)
    window.addEventListener('load', handler)
    window.addEventListener('scroll', handler)
    window.addEventListener('resize', handler)

    const body = document.getElementsByTagName("body")[0]
    const bodyScroll = body.scrollHeight > body.clientHeight
    if(bodyScroll) {
        body.addEventListener("scroll", handler)
    }

    return () => {
      window.removeEventListener('DOMContentLoaded', handler)
      window.removeEventListener('load', handler)
      window.removeEventListener('scroll', handler)
      window.removeEventListener('resize', handler)
      
      if(bodyScroll) {
        body.removeEventListener("scroll", handler)
      }
    }
  }, [handler])

  function toggleModal() {
    dispatch(toggleAccommodationModal(window.scrollY))
  }

  const onClickTabOption = (target, index) => {
    setCurTab(target)
    const targetElement = document.getElementById(target)
    const navBarHeight = _.get(actionBar, 'current.firstChild.clientHeight')
    if (targetElement) {
      // take into account the top nav AND the sticky bar height
      let yOffset = isSticky ? -90 : -navBarHeight - 90
      const body = document.getElementsByTagName("body")[0]
      const bodyScroll = body.scrollHeight > body.clientHeight
      const htmlStyles = window.getComputedStyle(document.getElementsByTagName("html")[0])
      if(bodyScroll && htmlStyles.getPropertyValue('overflow') !== 'visible') {
        const scrollToPosition = targetElement.getBoundingClientRect().top + body.scrollTop + yOffset
        body.scrollTo({top: scrollToPosition, behavior: 'smooth'})
      }
      else {
        const scrollToPosition = targetElement.getBoundingClientRect().top + window.pageYOffset + yOffset
        window.scrollTo({top: scrollToPosition, behavior: 'smooth'})
      }
    }
  }

  function deepLinkToSoftrip() {
    const softripDeepLinkURL = new URL(`${process.env.REACT_APP_BOOKING_BASE_URL}${softripBookingPath}`)
    softripDeepLinkURL.search = new URLSearchParams(softripBookingDetails)
    window.location.href = softripDeepLinkURL.toString()
  }

  function onRequestDatesPricing() {
    if (_.get(tour, 'softripBooking')) {
      deepLinkToSoftrip()
    } else {
      toggleModal()
    }
  }

  const limitNavBarOptions = _.get(tour, 'isGetaway') || _.get(tour, 'softripBooking')
  const options = limitNavBarOptions
      ? AMTRAK_GETAWAY_NAV_BAR_OPTIONS
      : NAV_BAR_OPTIONS

  const bookingButtonLabel = isRailBookers() || !_.get(tour, 'softripBooking')
    ? labels.availableDates
    : dimensions.width < 480
      ? labels.hometownPriceTruncated
      : labels.hometownPrice

  return (
      <div className={`tour-details-container__holder`} ref={actionBar}>
        <div id="tour-details-container__actions" className={`tour-details-container__actions ${isSticky ? 'sticky' : ''}`}>
            <div className={`tour-details-container__actions__tabs ${_.size(options) < 4 ? 'getaway-tabs' : ''}`}>
              {options.map((option, key) => {
                const tabClass = curTab === option.target ? 'active' : ''
                return (
                  <button 
                    className={`tour-details-container__actions__tabs__tab ${tabClass}`} 
                    key={key}
                    onClick={() => onClickTabOption(option.target, key)}>
                      {option.label}
                  </button>
                )
              })}
            </div>
            <div className="tour-details-container__actions__more">
            {isRailBookers() && (
              <div className="tour-details-container__actions__more__pricing">
                {`${labels.from} `}
                <div className="pricing-value">
                  {addBrandCurrencyPrefix(brand, _.get(tour, 'advertisedPrice'))}
                  <div className={`pricing-suffix ${brandCurrencySuffixes[brand] ? 'pricing-with-brand-suffix' : 'pricing-without-brand-suffix'}`}>
                    <span>{brandCurrencySuffixes[brand]}</span>
                    <span>pp</span>
                  </div>
                </div>
              </div>
            )}
          {(purlButtonInfo) && (
            <>
              {shouldDisplayEasyQuoteButton && (
                <button
                  className="tour-details-container__actions__more__button"
                  onClick={() => window.location.href = _.get(purlButtonInfo, 'purlUrl')}
                  onKeyDown={(event) => onEnter(event, () => window.location.href = _.get(purlButtonInfo, 'purlUrl'))}
                >
                  {_.get(purlButtonInfo, 'purlCta')}
                </button>
              )}
            </>
          )}
          {(!purlButtonInfo && displayBookButtons) && (
            <>
              {shouldDisplayEasyQuoteButton && (
                <button
                  className="tour-details-container__actions__more__button"
                  // onClick={() => window.location.href = getRAQFormUrl()}
                  // onKeyDown={event => onEnter(event, () => window.location.href = getRAQFormUrl())}
                  onClick={onRequestDatesPricing}
                  onKeyDown={(event) => onEnter(event, onRequestDatesPricing)}
                >
                  {labels.easyQuote}
                </button>
              )}
              <button
                className="tour-details-container__actions__more__button btn-secondary"
                onClick={onRequestDatesPricing}
                onKeyDown={(event) => onEnter(event, onRequestDatesPricing)}
              >
                {bookingButtonLabel}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default TourActions
